import { useCallback, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetches live Freightcom rates from the backend and exposes the quote
 * lifecycle to checkout / review pages.
 *
 * Returns:
 *   - rates:            array of top-N cheapest RateOption
 *   - package:          auto-estimated dims/weight used for the quote
 *   - mockMode:         true when FREIGHTCOM_API_KEY isn't configured (dev)
 *   - loading / error:  obvious
 *   - fetchRates(body): POST /api/shipping/rates  (triggers a quote)
 *   - saveQuote(body):  POST /api/shipping/save-quote  (locks it on order)
 */
export function useShippingRates() {
  const [rates, setRates] = useState([]);
  const [pkg, setPkg] = useState(null);
  const [shipFrom, setShipFrom] = useState(null);
  const [mockMode, setMockMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [totalCarriers, setTotalCarriers] = useState(0);
  const [distinctCarrierCount, setDistinctCarrierCount] = useState(0);

  const fetchRates = useCallback(async ({ shipTo, items, packageOverride, shipFrom: customShipFrom } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const body = { ship_to: shipTo, top_n: 3 };
      if (packageOverride) body.package = packageOverride;
      else if (items?.length) body.items = items;
      if (customShipFrom) body.ship_from = customShipFrom;

      const { data } = await axios.post(`${API_URL}/api/shipping/rates`, body);
      setRates(data.rates || []);
      setPkg(data.package);
      setShipFrom(data.ship_from);
      setMockMode(!!data.mock_mode);
      setFetchedAt(data.fetched_at);
      setTotalCarriers(data.total_carriers || (data.rates || []).length);
      setDistinctCarrierCount(data.distinct_carrier_count || 0);
      return data;
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to fetch shipping rates');
      setRates([]);
      setTotalCarriers(0);
      setDistinctCarrierCount(0);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const saveQuote = useCallback(async ({ orderId, orderNumber, shipTo, shipFrom: customShipFrom, package: packageData, selectedRate }) => {
    const body = {
      order_id: orderId,
      order_number: orderNumber,
      ship_to: shipTo,
      package: packageData,
      selected_rate: selectedRate,
    };
    if (customShipFrom) body.ship_from = customShipFrom;
    const { data } = await axios.post(`${API_URL}/api/shipping/save-quote`, body);
    return data;
  }, []);

  return { rates, package: pkg, shipFrom, mockMode, loading, error, fetchedAt, totalCarriers, distinctCarrierCount, fetchRates, saveQuote };
}
